<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import FaqService from '@/services/faq/faqService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { formatDate } from '@/utils/formatters.ts';

import type { FaqResponse } from '@/types/services/faq/faq.type.ts';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

const faqs = ref<FaqResponse[]>([]);
const loading = ref<boolean>(false);

const can = (permission: string): boolean => authStore.hasPermission([permission]);

async function fetchFaqs(): Promise<void> {
  loading.value = true;
  try {
    faqs.value = await FaqService.getAll();
  } finally {
    loading.value = false;
  }
}

onMounted(fetchFaqs);

const canReorder = (): boolean => can('EDIT_FAQ') && !loading.value;

// Optimistic reorder: the list is updated immediately, and reloaded from the API on failure
async function applyOrder(ids: number[]): Promise<void> {
  const previous: FaqResponse[] = faqs.value;
  const byId = new Map<number, FaqResponse>(previous.map((faq: FaqResponse) => [faq.id, faq]));
  faqs.value = ids.map((id: number) => byId.get(id) as FaqResponse);

  loading.value = true;
  try {
    faqs.value = await FaqService.reorder(ids);
    displayToast(t('views.faq.list.reorderSuccess'), 'success');
  } catch {
    // The order may be outdated (another admin changed the list): reload it
    faqs.value = previous;
    await fetchFaqs();
  } finally {
    loading.value = false;
  }
}

// --- Drag and drop (native HTML5, the row can only be dragged from its handle) --- //
const draggedId = ref<number | null>(null);
const dropSlot = ref<number | null>(null);
const dragEnabledId = ref<number | null>(null);

function onDragStart(event: DragEvent, faq: FaqResponse): void {
  draggedId.value = faq.id;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(faq.id));
  }
}

// `slot` is an insertion position between rows: 0 = before the first row, length = after the last one
function onDragOver(event: DragEvent, index: number): void {
  if (draggedId.value === null) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

  const row = event.currentTarget as HTMLElement;
  const { top, height } = row.getBoundingClientRect();
  dropSlot.value = event.clientY > top + height / 2 ? index + 1 : index;
}

function onDragOverEnd(event: DragEvent): void {
  if (draggedId.value === null) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  dropSlot.value = faqs.value.length;
}

function resetDrag(): void {
  draggedId.value = null;
  dropSlot.value = null;
  dragEnabledId.value = null;
}

async function onDrop(event: DragEvent): Promise<void> {
  event.preventDefault();
  const id: number | null = draggedId.value;
  let slot: number | null = dropSlot.value;
  resetDrag();
  if (id === null || slot === null || !canReorder()) return;

  const ids: number[] = faqs.value.map((faq: FaqResponse) => faq.id);
  const from: number = ids.indexOf(id);
  if (from === -1) return;

  if (from < slot) slot -= 1;
  if (slot === from) return;

  ids.splice(from, 1);
  ids.splice(slot, 0, id);
  await applyOrder(ids);
}

async function togglePublication(faq: FaqResponse): Promise<void> {
  const next: boolean = !faq.is_published;
  loading.value = true;
  try {
    const updated: FaqResponse = await FaqService.setPublication(faq.id, next);
    faqs.value = faqs.value.map((item: FaqResponse) => (item.id === updated.id ? updated : item));
    displayToast(t(next ? 'views.faq.list.publishSuccess' : 'views.faq.list.unpublishSuccess'), 'success');
  } finally {
    loading.value = false;
  }
}

async function removeFaq(faq: FaqResponse): Promise<void> {
  const options: ModalOptions = {
    title: t('views.faq.list.deleteModal.title'),
    message: t('views.faq.list.deleteModal.message'),
    validateButton: t('common.buttons.validate'),
    cancelButton: t('common.buttons.cancel'),
  };

  if (!(await displayModal(options))) return;

  loading.value = true;
  try {
    await FaqService.remove(faq.id);
    displayToast(t('views.faq.list.deleteSuccess'), 'success');
    await fetchFaqs();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <MainTitleComponent icon="PhQuestion">
    {{ t('views.faq.list.title') }}
    <template #actions>
      <v-col v-if="can('ADD_FAQ')" cols="12" sm="auto">
        <v-btn color="primary" prepend-icon="PhPlus" variant="flat" :to="{ name: 'faqCreate' }">
          {{ t('views.faq.list.add') }}
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-table class="mt-4">
    <thead>
      <tr>
        <th class="text-left">{{ t('views.faq.list.columns.order') }}</th>
        <th class="text-left">{{ t('views.faq.list.columns.question') }}</th>
        <th class="text-left">{{ t('views.faq.list.columns.status') }}</th>
        <th class="text-left">{{ t('views.faq.list.columns.updatedAt') }}</th>
        <th class="text-right">{{ t('views.faq.list.columns.actions') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="!loading && faqs.length === 0">
        <td colspan="5" class="text-center py-6">{{ t('views.faq.list.empty') }}</td>
      </tr>
      <tr
        v-for="(faq, index) in faqs"
        :key="faq.id"
        :class="{
          'faq-dragged': draggedId === faq.id,
          'faq-drop-target': dropSlot === index
        }"
        :draggable="dragEnabledId === faq.id"
        @dragend="resetDrag"
        @dragover="onDragOver($event, index)"
        @dragstart="onDragStart($event, faq)"
        @drop="onDrop($event)"
      >
        <td style="white-space: nowrap">
          <v-icon
            v-if="can('EDIT_FAQ')"
            :aria-label="t('views.faq.list.drag')"
            :class="loading ? 'faq-handle-disabled' : 'faq-handle'"
            icon="PhDotsSixVertical"
            @mousedown="dragEnabledId = canReorder() ? faq.id : null"
            @mouseup="dragEnabledId = null"
          />
        </td>
        <td>{{ faq.question }}</td>
        <td>
          <v-switch
            :disabled="loading || !can('EDIT_FAQ')"
            :label="faq.is_published ? t('views.faq.list.published') : t('views.faq.list.draft')"
            :model-value="faq.is_published"
            color="primary"
            density="compact"
            hide-details
            inset
            @update:model-value="togglePublication(faq)"
          />
        </td>
        <td>{{ formatDate(faq.updated_at) }}</td>
        <td class="text-right" style="white-space: nowrap">
          <v-btn
            v-if="can('EDIT_FAQ')"
            :aria-label="t('views.faq.list.edit')"
            density="comfortable"
            icon="PhPencilSimple"
            size="small"
            variant="text"
            @click="router.push({ name: 'faqEdit', params: { id: faq.id } })"
          />
          <v-btn
            v-if="can('DELETE_FAQ')"
            :aria-label="t('views.faq.list.delete')"
            :disabled="loading"
            color="error"
            density="comfortable"
            icon="PhTrash"
            size="small"
            variant="text"
            @click="removeFaq(faq)"
          />
        </td>
      </tr>
      <!-- Drop zone after the last row, so an item can be moved to the very bottom -->
      <tr
        v-if="draggedId !== null"
        :class="{ 'faq-drop-target': dropSlot === faqs.length }"
        @dragover="onDragOverEnd($event)"
        @drop="onDrop($event)"
      >
        <td class="faq-end-zone" colspan="5" />
      </tr>
    </tbody>
  </v-table>
</template>

<style scoped>
.faq-handle {
  cursor: grab;
}

.faq-handle-disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.faq-dragged {
  opacity: 0.4;
}

.faq-end-zone {
  height: 48px;
}

.faq-drop-target td {
  border-top: 2px solid rgb(var(--v-theme-primary));
}
</style>
